import { atom, selector } from 'recoil'
import { ApiErrorResponse, getKyousouApiClient } from '../services/network'
import { AuthenticatedUser } from '../clients/kyousou_api_client'

const AUTH_TOKEN_KEY = 'KYOUSOU_AUTH_TOKEN'

type AuthState = {
  isAuthenticated: boolean
  user: AuthenticatedUser | null
  token: string | null
  loading: boolean
  initialized: boolean
}

/**
 * 認証情報を管理するatom
 */
export const authState = atom<AuthState>({
  key: 'authState',
  default: {
    isAuthenticated: false,
    user: null as AuthenticatedUser | null,
    token: null as string | null,
    loading: true,
    initialized: false,
  },
})

/**
 * 認証情報を初期化するselector
 */
export const authInitializer = selector({
  key: 'authInitializer',
  get: async ({ get }): Promise<AuthState> => {
    const currentAuth = get(authState)

    // 既に初期化済みで認証済みなら現在の状態を返す
    if (
      currentAuth.initialized &&
      currentAuth.isAuthenticated &&
      currentAuth.user
    ) {
      return currentAuth
    }

    const token = getToken()

    // トークンが有効かAPIで確認する
    if (token) {
      const apiClient = getKyousouApiClient()
      const responseUser = await apiClient.auth
        .checkCreate()
        .then((res) => {
          return res.data.user
        })
        .catch((e: ApiErrorResponse) => {
          console.error(e)
          return null
        })

      // ユーザー情報が取得できた場合は認証情報を更新
      if (responseUser !== null) {
        return {
          isAuthenticated: true,
          user: responseUser,
          token,
          loading: false,
          initialized: true,
        }
      }
    }

    clearToken()
    return {
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      initialized: true,
    }
  },
})

/**
 * ローカルストレージからトークンを取得する
 * @returns トークン
 */
export const getToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

export const setToken = (token: string): void => {
  localStorage.setItem(AUTH_TOKEN_KEY, token)
}

export const clearToken = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY)
}

/**
 * ログイン中のユーザー情報を取得するselector
 */
export const currentUserSelector = selector({
  key: 'currentUserSelector',
  get: ({ get }) => {
    const auth = get(authState)
    return auth.user
  },
})

/**
 * 認証状態をチェックするselector
 */
export const isAuthenticatedSelector = selector({
  key: 'isAuthenticatedSelector',
  get: ({ get }) => {
    const auth = get(authState)
    return auth.isAuthenticated
  },
})
